"use client"

import * as React from "react"
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes"

import type { ThemeColorMode } from "@workspace/model"

/**
 * `colorMode` is the tenant's `theme.colorMode` (already narrowed): `both`
 * follows the visitor's system preference / saved choice; `light` or `dark`
 * becomes next-themes' `forcedTheme`, which pins the `class` on <html> (the
 * pre-paint script honours it too, so no flash) and leaves the visitor's saved
 * preference untouched. The toggle and the `d` hotkey are off while forced.
 */
function ThemeProvider({
  children,
  colorMode = "both",
  ...props
}: React.ComponentProps<typeof NextThemesProvider> & {
  colorMode?: ThemeColorMode
}) {
  const forcedTheme = colorMode === "both" ? undefined : colorMode

  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      forcedTheme={forcedTheme}
      {...props}
    >
      {forcedTheme ? null : <ThemeHotkey />}
      {children}
    </NextThemesProvider>
  )
}

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  )
}

function ThemeHotkey() {
  const { resolvedTheme, setTheme } = useTheme()

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || event.repeat) {
        return
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      if (event.key?.toLowerCase() !== "d") {
        return
      }

      if (isTypingTarget(event.target)) {
        return
      }

      setTheme(resolvedTheme === "dark" ? "light" : "dark")
    }

    window.addEventListener("keydown", onKeyDown)

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [resolvedTheme, setTheme])

  return null
}

export { ThemeProvider }
